import React, { useState } from 'react';
import Uppy from '@uppy/core';
import { UppyContextProvider } from '@uppy/react';
import AwsS3 from '@uppy/aws-s3';
import ThumbnailGenerator from '@uppy/thumbnail-generator';

import {
  APIupdateAsset,
  APIdeleteAsset,
} from '../../../../api/Assets';

import DashboardAdminAssetsAddItem from './AddItem';
import DashboardAdminAssetsDropzone from './Dropzone';

import {
  getFileMetadata,
  isImageType,
} from '../../../../helpers/file';

import '@uppy/core/css/style.css';
import '@uppy/react/css/style.css';

import { Asset } from '../../../../types/asset';
import { AssetGroup } from '../../../../types/asset-group';

interface DashboardAdminAssetsUploadProps {
  addFile: Function;
  removeFile: Function;
  setUploading: Function;
  setFiles: Function;
  assets: Asset[];
  assetGroups: AssetGroup[];
  handleChangeFile: Function;
}

const DashboardAdminAssetsUpload: React.FC<DashboardAdminAssetsUploadProps> = ({
  addFile,
  removeFile,
  setUploading,
  setFiles,
  assets,
  assetGroups,
  handleChangeFile,
}) => {
  const [uppy] = useState(() => new Uppy({
    autoProceed: true,
    restrictions: {
      allowedFileTypes: ['.jpg', '.jpeg', '.png'],
      maxNumberOfFiles: 100,
    },
  })
  .on('file-removed', (file) => {
    APIdeleteAsset(file.meta.uuid as string);
    removeFile(file.meta.uuid);
    setFiles(uppy.getFiles());
  })
  .on('file-added', () => {
    setFiles(uppy.getFiles());
  })
  .on('progress', () => {
    setFiles(uppy.getFiles());
  })
  .on('upload', () => {
    setUploading(true);
  })
  .on('upload-progress', () => {
    setFiles(uppy.getFiles());
  })
  .on('upload-success', async (file) => {
    if (file) {
      const metadata = isImageType(file.type) ? await getFileMetadata(file.data as File) : {};
      APIupdateAsset(file.meta.uuid as string, {
        status: 'uploaded',
        shared: true,
        metadata,
      } as Partial<Asset>);
      addFile(file.meta.uuid);
    }
    setFiles(uppy.getFiles());
    setUploading(false);
  })
  .on('upload-error', () => {
    setFiles(uppy.getFiles());
    setUploading(false);
  })
  .on('thumbnail:generated', () => {
    setFiles(uppy.getFiles());
  })
  .use(ThumbnailGenerator, {
    thumbnailType: 'image/png',
  })
  .use(AwsS3, {
    shouldUseMultipart: false,
    getUploadParameters: async (file) => {
      return window.axios('/api/assets/upload', {
        method: 'post',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
        },
        data: JSON.stringify({
          filename: file.name,
          contentType: file.type,
          size: file.size,
        }),
      })
        .then((response) => {
          return response.data;
        })
        .then((data) => {
          uppy.setFileMeta(file.id, {...data.fields});

          return {
            method: data.method,
            url: data.url,
            fields: data.fields,
            headers: data.headers,
          }
        });
    },
  }));

  const handleRemoveFile = (fileId: string) => {
    const file = assets.find((f) => f.id === fileId);
    if (file?.local) {
      uppy.removeFile(file.uppyFileId as string);
    }
  };

  return (
    <div className="dashboard-assets-uploader-container">
      <div className="dashboard-assets">
        <UppyContextProvider uppy={uppy}>
          <div className="dashboard-assets-uploader">
            <DashboardAdminAssetsDropzone />
            <div className="dashboard-admin-assets-add-list wa-cluster wa-gap-1">
              {assets.length > 0 && (
                <table>
                  <thead>
                    <tr>
                      <th>&nbsp;</th>
                      <th>Title</th>
                      <th>Groups</th>
                      <th>Hidden</th>
                      <th className="actions">&nbsp;</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assets.map(file => (
                      <DashboardAdminAssetsAddItem
                        key={String(file.id)}
                        asset={file as Asset}
                        assetGroups={assetGroups}
                        handleChange={handleChangeFile}
                        handleDelete={handleRemoveFile}
                      />
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </UppyContextProvider>
      </div>
    </div>
  );
}

export default DashboardAdminAssetsUpload;
